import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  LinearProgress,
} from "@mui/material";
import {
  CheckCircle,
  Error as ErrorIcon,
  Download,
  Refresh,
} from "@mui/icons-material";

/**
 * Reusable ImportResults component for displaying import results
 * 
 * @param {Object} results - Import results: { totalRows, success, failed, errors }
 * @param {Function} onDownloadErrors - Download error report handler
 * @param {Function} onRetry - Retry import handler
 * @param {Function} onClose - Close handler
 */
const ImportResults = ({
  results = {},
  onDownloadErrors,
  onRetry,
  onClose,
}) => {
  const { totalRows = 0, success = 0, failed = 0, errors = [] } = results;
  const successRate = totalRows > 0 ? (success / totalRows) * 100 : 0;

  return (
    <Box sx={{ textAlign: "center", py: 4 }}>
      {results.loading ? (
        <Box>
          <LinearProgress sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Importing data...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait while we process your file
          </Typography>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              bgcolor: failed === 0 ? "success.light" : "warning.light",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            {failed === 0 ? (
              <CheckCircle sx={{ fontSize: 40, color: "success.main" }} />
            ) : (
              <ErrorIcon sx={{ fontSize: 40, color: "warning.main" }} />
            )}
          </Box>

          <Typography variant="h5" fontWeight={600} gutterBottom>
            {failed === 0 ? "Import Successful!" : "Import Completed with Errors"}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            {failed === 0
              ? `All ${success} records were imported successfully.`
              : `${success} records imported successfully, ${failed} failed.`}
          </Typography>

          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={4}>
              <Card>
                <CardContent>
                  <Typography variant="h4" fontWeight={700} color="primary">
                    {totalRows}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total Rows
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={4}>
              <Card>
                <CardContent>
                  <Typography variant="h4" fontWeight={700} color="success.main">
                    {success}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Successful
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={4}>
              <Card>
                <CardContent>
                  <Typography variant="h4" fontWeight={700} color="error.main">
                    {failed}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Failed
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Success Rate
            </Typography>
            <LinearProgress
              variant="determinate"
              value={successRate}
              sx={{ height: 8, borderRadius: 4 }}
              color={successRate === 100 ? "success" : "warning"}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {successRate.toFixed(1)}%
            </Typography>
          </Box>

          {errors.length > 0 && (
            <Card variant="outlined" sx={{ mt: 3, textAlign: "left" }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Errors ({errors.length})
                </Typography>
                <List dense>
                  {errors.slice(0, 5).map((error, index) => (
                    <React.Fragment key={index}>
                      <ListItem>
                        <ListItemText
                          primary={
                            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                              <Chip
                                label={`Row ${error.row}`}
                                size="small"
                                color="error"
                                variant="outlined"
                              />
                              <Typography variant="body2">
                                {error.field}: {error.error}
                              </Typography>
                            </Box>
                          }
                          secondary={error.value && `Value: ${error.value}`}
                        />
                      </ListItem>
                      {index < Math.min(errors.length, 5) - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
                {errors.length > 5 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    ... and {errors.length - 5} more errors
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}

          <Box sx={{ display: "flex", gap: 2, justifyContent: "center", mt: 4 }}>
            {onDownloadErrors && errors.length > 0 && (
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={onDownloadErrors}
              >
                Download Error Report
              </Button>
            )}
            {onRetry && failed > 0 && (
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={onRetry}
              >
                Retry Failed Rows
              </Button>
            )}
            <Button variant="contained" onClick={onClose}>
              Done
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default ImportResults;

